import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | Donna Gordon Psychotherapy';
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="privacy-page">
      {/* Slim header */}
      <header className="border-b border-[#D1D8D0] bg-[#FAF9F6]/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="block group" data-testid="privacy-home-link">
            <h1 className="font-heading text-xl text-[#2C362A] group-hover:text-[#708A72] transition-colors">Donna Gordon</h1>
            <p className="font-body text-xs text-[#708A72]">Psychotherapist, MA</p>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-[#708A72] hover:text-[#5D755F] transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft size={15} strokeWidth={1.5} />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h1 className="font-heading text-4xl md:text-5xl text-[#2C362A] mb-3">Privacy Policy</h1>
        <p className="font-body text-sm text-[#708A72] mb-12">Last updated: 28 April 2026</p>

        <div className="font-body text-base text-[#2C362A] leading-relaxed space-y-6">

          <section>
            <p className="text-[#5B6E56]">
              This Privacy Policy explains how Donna Gordon (&ldquo;Donna&rdquo;, &ldquo;I&rdquo;, &ldquo;me&rdquo;, &ldquo;my&rdquo;) collects,
              uses, and protects your personal information when you visit
              <a href="https://donnagordon.ie" className="text-[#708A72] underline hover:no-underline"> donnagordon.ie</a> (the &ldquo;website&rdquo;)
              or contact me through it. I am committed to protecting your privacy and complying
              with the EU General Data Protection Regulation (GDPR), the Irish Data Protection Act 2018,
              and the ethical guidelines of the Irish Association for Counselling &amp; Psychotherapy (IACP).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">1. Who I am (Data Controller)</h2>
            <p className="text-[#5B6E56]">
              For the purposes of GDPR, the data controller is:
            </p>
            <div className="mt-3 bg-white border border-[#D1D8D0] rounded-2xl p-5 text-[#5B6E56] space-y-2">
              <p className="font-semibold text-[#2C362A]">Donna Gordon, MA &mdash; Psychotherapist</p>
              <p className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} className="text-[#708A72]" />Mynd, 5 Albert Walk, Bray, Co. Wicklow, A98 HP71</p>
              <p className="flex items-center gap-2"><Mail size={14} strokeWidth={1.5} className="text-[#708A72]" /><a href="mailto:donnagordonpsychotherapy@gmail.com" className="text-[#708A72] underline hover:no-underline">donnagordonpsychotherapy@gmail.com</a></p>
              <p className="flex items-center gap-2"><Phone size={14} strokeWidth={1.5} className="text-[#708A72]" /><a href="tel:+353894475225" className="text-[#708A72] underline hover:no-underline">+353 89 447 5225</a></p>
              <p className="text-xs pt-1">IACP Registered Member</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">2. What information I collect</h2>
            <p className="text-[#5B6E56] mb-3">
              When you submit the contact form on this website, I collect the information you provide:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-1 text-[#5B6E56]">
              <li>Your full name</li>
              <li>Your pronouns (optional)</li>
              <li>Your email address</li>
              <li>Your phone number (optional)</li>
              <li>The message you choose to share</li>
              <li>Your preferred days/times for an introductory call (optional)</li>
              <li>The date and time the message was sent</li>
            </ul>
            <p className="text-[#5B6E56] mt-4">
              I do not use cookies, advertising trackers, or third-party analytics on this website.
              The website does not collect IP addresses, location data, or browsing behaviour.
            </p>
            <p className="text-[#5B6E56] mt-3">
              <strong className="text-[#2C362A]">Please do not share highly sensitive personal,
              medical, or mental-health information through the initial contact form.</strong> A brief
              message about what you are looking for is enough &mdash; we can explore anything sensitive
              securely once we are in direct contact.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">3. Why I collect it (Purpose &amp; Legal basis)</h2>
            <p className="text-[#5B6E56] mb-3">I use your information solely to:</p>
            <ul className="list-disc list-outside pl-5 space-y-1 text-[#5B6E56]">
              <li>Respond to your enquiry</li>
              <li>Arrange a free introductory call, if you would like one</li>
              <li>Schedule and provide therapy sessions, if you decide to proceed</li>
            </ul>
            <p className="text-[#5B6E56] mt-4">
              The legal basis for processing your enquiry data is your <strong className="text-[#2C362A]">consent</strong> (GDPR
              Article 6(1)(a)), which you give by ticking the consent checkbox before submitting the form.
              You may withdraw consent at any time by emailing me at the address above.
            </p>
            <p className="text-[#5B6E56] mt-3">
              If you become a client, additional processing takes place under <strong className="text-[#2C362A]">contract</strong> (GDPR
              Article 6(1)(b)) and, where any health-related information is involved, under <strong className="text-[#2C362A]">provision
              of healthcare</strong> (GDPR Article 9(2)(h)). Any such processing is covered by a separate
              client agreement provided at the start of therapy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">4. How long I keep your data</h2>
            <p className="text-[#5B6E56]">
              Contact-form enquiries that do not become client engagements are deleted within
              <strong className="text-[#2C362A]"> 12 months</strong>. If you become a client, clinical records are retained for the
              period required by IACP guidelines and Irish professional/legal standards (typically
              7 years from the end of therapy, or longer where required by law). Details are shared
              in the client agreement at the start of therapy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">5. Who has access to your data</h2>
            <p className="text-[#5B6E56] mb-3">
              I treat your information as strictly confidential. Only Donna Gordon reads enquiries.
              I use the following carefully chosen service providers (data processors), each bound
              by appropriate Data Processing Agreements:
            </p>
            <ul className="list-disc list-outside pl-5 space-y-1 text-[#5B6E56]">
              <li><strong className="text-[#2C362A]">EmailJS</strong> &mdash; transmits the contact form to my inbox (USA, with EU Standard Contractual Clauses)</li>
              <li><strong className="text-[#2C362A]">Google (Gmail)</strong> &mdash; stores my professional inbox (EU/USA, with EU Standard Contractual Clauses)</li>
              <li><strong className="text-[#2C362A]">Vercel</strong> &mdash; hosts the website (USA, with EU Standard Contractual Clauses)</li>
              <li><strong className="text-[#2C362A]">Blacknight</strong> &mdash; manages the donnagordon.ie domain (Ireland)</li>
            </ul>
            <p className="text-[#5B6E56] mt-4">
              I will never sell, rent, or share your information with anyone for marketing or
              advertising purposes. I will only disclose information to third parties where
              required by law (e.g. to safeguard a person at serious risk of harm, or in response
              to a valid legal order) and, where appropriate, in line with the IACP Code of Ethics.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">6. International transfers</h2>
            <p className="text-[#5B6E56]">
              Some of my service providers (EmailJS, Google, Vercel) are based in the United States.
              Where personal data is transferred outside the European Economic Area, the transfer
              is protected by the European Commission&rsquo;s Standard Contractual Clauses and supplementary
              technical measures (encryption in transit and at rest).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">7. Your rights under GDPR</h2>
            <p className="text-[#5B6E56] mb-3">You have the right to:</p>
            <ul className="list-disc list-outside pl-5 space-y-1 text-[#5B6E56]">
              <li>Access the personal data I hold about you</li>
              <li>Request correction of inaccurate or incomplete data</li>
              <li>Request erasure of your data (&ldquo;right to be forgotten&rdquo;)</li>
              <li>Restrict or object to processing</li>
              <li>Receive your data in a portable format</li>
              <li>Withdraw your consent at any time</li>
            </ul>
            <p className="text-[#5B6E56] mt-4">
              To exercise any of these rights, please email
              <a href="mailto:donnagordonpsychotherapy@gmail.com" className="text-[#708A72] underline hover:no-underline"> donnagordonpsychotherapy@gmail.com</a>.
              I will respond within one month, in line with GDPR.
            </p>
            <p className="text-[#5B6E56] mt-3">
              You also have the right to lodge a complaint with the Irish supervisory authority,
              the <strong className="text-[#2C362A]">Data Protection Commission (DPC)</strong> &mdash;
              <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" className="text-[#708A72] underline hover:no-underline"> dataprotection.ie</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">8. Security</h2>
            <p className="text-[#5B6E56]">
              The website is served entirely over HTTPS. Form submissions are encrypted in
              transit. I follow industry-standard practices to protect access to my professional
              email account, including strong passwords and two-factor authentication.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">9. Children</h2>
            <p className="text-[#5B6E56]">
              This website and my services are intended for adults aged 18 and over. I do not
              knowingly collect data from children through this website.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">10. Confidentiality &amp; professional standards</h2>
            <p className="text-[#5B6E56]">
              Beyond GDPR, I am bound by the IACP Code of Ethics and Practice. Therapy content is
              held in the strictest confidence, with the standard ethical exceptions (risk of
              serious harm, mandatory reporting under Irish law, or written legal request). These
              are explained fully in the client agreement before therapy begins.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">11. Changes to this policy</h2>
            <p className="text-[#5B6E56]">
              I may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date at the
              top of this page reflects the most recent revision. Material changes will be
              communicated through the website.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl text-[#2C362A] mt-10 mb-3">12. Contact</h2>
            <p className="text-[#5B6E56]">
              For any questions about this Privacy Policy or how your data is handled, please
              contact me at
              <a href="mailto:donnagordonpsychotherapy@gmail.com" className="text-[#708A72] underline hover:no-underline"> donnagordonpsychotherapy@gmail.com</a>.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8 border-t border-[#D1D8D0]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-[#708A72] hover:text-[#5D755F] transition-colors"
            data-testid="privacy-bottom-back-link"
          >
            <ArrowLeft size={15} strokeWidth={1.5} />
            Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
